import { useCreateCentre, useMainCentreList } from '@features/settings';
import { CANCEL_BTN, FIELD, LABEL, SAVE_BTN } from '@features/settings/styles';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { useAppShellStore } from '@store/appShellStore';
import { useState } from 'react';

interface CentreFormState {
  mainCentreId: string;
  name: string;
  code: string;
}

const emptyForm = (mainCentreId: string): CentreFormState => ({ mainCentreId, name: '', code: '' });

interface AddCentreDialogProps {
  open: boolean;
  onClose: () => void;
}

type FieldErrors = Partial<Record<keyof CentreFormState, string>>;

/**
 * Nested "New Centre" dialog, opened from ManageCentresDialog's header.
 *
 * Just a name and a code: the receipt identity (legal name, address, contact,
 * GSTIN) belongs to the parent main centre and is shared by all of its centres,
 * so there is nothing per-centre left to collect. Defaults to the main centre
 * selected in the top bar, which is what an admin adding a branch nearly always
 * wants.
 */
export const AddCentreDialog = ({ open, onClose }: AddCentreDialogProps) => {
  const activeMainCentreId = useAppShellStore((s) => s.mainCentreId);
  const { data: mainCentres, isLoading: mainCentresLoading } = useMainCentreList();
  const [form, setForm] = useState<CentreFormState>(() => emptyForm(activeMainCentreId));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const createCentre = useCreateCentre();

  const selectedMainCentre = (mainCentres ?? []).find((m) => m._id === form.mainCentreId);

  const handleClose = () => {
    onClose();
    setForm(emptyForm(activeMainCentreId));
    setFieldErrors({});
  };

  const setField = (key: keyof CentreFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = () => {
    const errors: FieldErrors = {};
    if (!form.mainCentreId) errors.mainCentreId = 'Main Centre is required';
    if (!form.name.trim()) errors.name = 'Centre Name is required';
    if (!form.code.trim()) errors.code = 'Centre Code is required';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    createCentre.mutate(
      { mainCentreId: form.mainCentreId, name: form.name.trim(), code: form.code.trim() },
      { onSuccess: handleClose },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{ paper: { sx: { borderRadius: '16px', p: '8px', width: { xs: 'calc(100vw - 32px)', sm: 520 }, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <DialogTitle sx={(theme) => ({ fontSize: 16, fontWeight: 700, color: theme.palette.grey[900], pb: '4px' })}>
        Add New Centre
      </DialogTitle>
      <DialogContent>
        <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.grey[500], mb: '16px' })}>
          {selectedMainCentre?.instituteName
            ? `Receipts from this centre are issued under ${selectedMainCentre.instituteName} — the main centre's name, address and GSTIN.`
            : "Receipts from this centre are issued under its main centre's name, address and GSTIN."}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '16px' }}>
          <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
            <Typography component="label" sx={LABEL}>
              Main Centre
              <Box component="span" sx={(theme) => ({ color: theme.palette.error.main })}> *</Box>
            </Typography>
            <TextField
              select
              value={form.mainCentreId}
              onChange={setField('mainCentreId')}
              fullWidth
              disabled={mainCentresLoading}
              error={!!fieldErrors.mainCentreId}
              helperText={fieldErrors.mainCentreId}
              sx={FIELD}
            >
              {(mainCentres ?? []).length === 0 && (
                <MenuItem disabled value="">No main centres — add one first</MenuItem>
              )}
              {(mainCentres ?? []).map((m) => (
                <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography component="label" sx={LABEL}>
              Centre Name
              <Box component="span" sx={(theme) => ({ color: theme.palette.error.main })}> *</Box>
            </Typography>
            <TextField
              value={form.name}
              onChange={setField('name')}
              fullWidth
              placeholder="e.g. GK-CENTRE"
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
              sx={FIELD}
            />
          </Box>

          <Box>
            <Typography component="label" sx={LABEL}>
              Centre Code
              <Box component="span" sx={(theme) => ({ color: theme.palette.error.main })}> *</Box>
            </Typography>
            <TextField
              value={form.code}
              onChange={setField('code')}
              fullWidth
              placeholder="e.g. GK"
              error={!!fieldErrors.code}
              helperText={fieldErrors.code}
              sx={FIELD}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
        <Button variant="outlined" sx={CANCEL_BTN} onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={createCentre.isPending} sx={SAVE_BTN}>
          {createCentre.isPending ? <CircularProgress size={16} color="inherit" /> : 'Add Centre'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
